import {
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
  useContext,
} from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { Button, Input, RadioButton } from '@appsumo/dorado-react';

import { SurveyContext } from '~/contexts/survey';
import { Question, Survey } from '~/types/survey/types';
import {
  MD_BREAKPOINT_MAX_WIDTH,
  SURVEY_DISMISS_TIMEOUT,
} from '~/constants/survey';
import { SurveyContainer } from '~/components/survey/SurveyContainer';

interface PurchaseSurveyComponentProps {
  survey: Survey;
}

type OffsetHeightWidth = {
  offsetHeight: number;
  offsetWidth: number;
};

type FollowUpQuestionResponses = {
  id: number;
  text: string;
};

type SelectedChoices = Array<{
  id: number;
  value: string;
  followUpQuestion: FollowUpQuestionResponses;
}>;

const FollowUpInputText = memo(function InputText({
  question,
  choiceId,
  isDisabled,
  onChange,
}: Readonly<{
  question?: Question;
  choiceId: number;
  isDisabled: boolean;
  onChange: (value: string) => void;
}>) {
  if (!question) {
    return null;
  }

  return (
    <Input
      data-testid={`survey-choice-input-${choiceId}`}
      name={`survey-choice-input-${choiceId}`}
      label={question.text}
      required={question.required}
      disabled={isDisabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
});

export default memo(function RadioList({
  survey,
}: Readonly<PurchaseSurveyComponentProps>) {
  const isMobile = useMediaQuery(`(max-width: ${MD_BREAKPOINT_MAX_WIDTH}px)`);
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(undefined);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedChoices, setSelectedChoices] = useState<SelectedChoices>([]);
  const [containerOffsets, setContainerOffsets] = useState<OffsetHeightWidth>({
    // Prevent weird transition on first render
    offsetHeight: 1000,
    offsetWidth: 1000,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { isProcessing, dismiss, submit } = useContext(SurveyContext);

  const question = useMemo(() => {
    return survey.questions?.[0] ?? {};
  }, [survey.questions]);

  const title = useMemo(() => {
    return isSubmitted
      ? 'Thank you! Your response has been recorded'
      : question.text;
  }, [isSubmitted, question.text]);

  // Create style object based on mobile/desktop
  // Note: Calculation wrapped in Number() to avoid -0 value and
  // fix test failures when 0 !== -0
  const styleObject: Record<string, string | undefined> = useMemo(() => {
    if (isMobile) {
      return {
        bottom: isExpanded
          ? '0'
          : `${Number(containerOffsets.offsetHeight * -1)}px`,
      };
    } else {
      return {
        // Add 2 to the width to adjust for the border
        right: isExpanded
          ? '0'
          : `${Number(containerOffsets.offsetWidth + 2) * -1}px`,
      };
    }
  }, [isMobile, isExpanded, containerOffsets]);

  const areInputValid = useCallback(() => {
    // question.choices and selectedChoices have the same indexes
    return selectedChoices.every((x, index) => {
      const isRequired = question.choices[index].follow_up_question?.required;

      if (!x || !isRequired) {
        return true;
      }

      return isRequired && x.followUpQuestion?.text;
    });
  }, [question.choices, selectedChoices]);

  const handleRadioButtonChange = useCallback(
    (id: number, value: string, index: number) => {
      const updatedChoices = [...selectedChoices];
      updatedChoices[index] = {
        id,
        value,
        followUpQuestion: {} as FollowUpQuestionResponses,
      };

      setSelectedChoices(updatedChoices);
    },
    [selectedChoices],
  );

  const handleSubmit = useCallback(() => {
    // Filter out any empty choices
    const selectedChoicesFiltered = selectedChoices.filter((x) => Boolean(x));

    submit({
      id: survey.id,
      questionId: question.id,
      choiceIds: selectedChoicesFiltered.map((x) => x.id),
      followUpQuestions: selectedChoicesFiltered.map((x) => ({
        id: x.followUpQuestion.id,
        text: x.followUpQuestion.text,
      })),
    });
    setIsSubmitted(true);
  }, [question.id, selectedChoices, submit, survey.id]);

  const handleDismiss = useCallback(() => {
    dismiss(survey.id);
  }, [dismiss, survey.id]);

  const handleFeedbackClick = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const handleInputChange = useCallback(
    (id: number, text: string, index: number) => {
      const updatedAnswers = [...selectedChoices];

      updatedAnswers[index].followUpQuestion = {
        id,
        text,
      };
      setSelectedChoices(updatedAnswers);
    },
    [selectedChoices],
  );

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    setContainerOffsets({
      offsetHeight: containerRef.current.offsetHeight,
      offsetWidth: containerRef.current.offsetWidth,
    });
  }, []);

  // Open survey after a few seconds
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIsExpanded(true);
    }, SURVEY_DISMISS_TIMEOUT);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!survey.questions.length) {
    return null;
  }

  return (
    <SurveyContainer
      style={styleObject}
      title={title}
      isSubmitted={isSubmitted}
      className={`bottom-0 right-0 w-full transition-all duration-500 md:bottom-auto md:top-1/2 md:w-[430px] md:-translate-y-1/2`}
      onClose={handleDismiss}
      refValue={containerRef}
    >
      <div className="flex max-h-screen w-full flex-col gap-y-4 overflow-y-auto">
        <button
          className="absolute top-[-30px] left-1/2 -translate-x-1/2 rounded rounded-b-none bg-forest py-1 px-2
          text-white md:top-1/2 md:left-[-17px] md:-translate-y-1/2 md:-rotate-90"
          onClick={handleFeedbackClick}
        >
          Feedback
        </button>
        <div className="flex flex-col justify-start gap-y-4">
          {question.choices.map((choice, index) => (
            <>
              <RadioButton
                data-testid={`survey-choice-radio-${choice.id}`}
                key={choice.id}
                name={`survey-choice-radio-${choice.id}`}
                label={choice.label}
                value={choice.label}
                defaultChecked={!!selectedChoices[index]?.value}
                // @ts-ignore
                onChange={() =>
                  handleRadioButtonChange(choice.id, choice.label, index)
                }
              />
              {selectedChoices.find((x) => x?.value === choice.label) && (
                <FollowUpInputText
                  choiceId={choice.id}
                  question={choice.follow_up_question}
                  isDisabled={isSubmitted}
                  onChange={(text) =>
                    handleInputChange(
                      choice.follow_up_question?.id ?? -1,
                      text,
                      index,
                    )
                  }
                />
              )}
            </>
          ))}
        </div>
        {!isSubmitted && (
          <Button
            onClick={handleSubmit}
            disabled={
              isProcessing || selectedChoices.length === 0 || !areInputValid()
            }
          >
            Submit
          </Button>
        )}
      </div>
    </SurveyContainer>
  );
});
